/**
 * Acoustic features for telling near-field speech (the user, close to the mic)
 * apart from far-field speech (other people in the room).
 *
 * Loudness alone cannot do this: a person a metre away speaks at roughly the same
 * RMS as the user, so the two distributions overlap and no threshold separates
 * them. These features target cues that survive that overlap.
 *
 *   decay slope  Energy collapses within a frame or two when close speech stops.
 *                A voice across the room leaves a reverberant tail, so it decays
 *                at roughly the room's RT60 instead. Unlike level, this holds even
 *                when the other person is shouting.
 *   modulation   Depth of the valleys between syllables. Close speech has real
 *                silence in the gaps; reverb and room babble fill them in for a
 *                distant talker.
 *   spectral tilt Distant voices lose highs to room absorption and head
 *                directivity, and directional mics gain bass at close range
 *                (proximity effect). Device-dependent, so a soft cue only.
 *
 * Onset sharpness is deliberately weak here: the direct sound arrives first at any
 * distance, so rise time barely differs. It is recorded for completeness only.
 *
 * Nothing in this module changes what the microphone sends to OpenAI — it measures.
 */

/**
 * Sized for frequency resolution, not for the level meter: 2048 bins at 48kHz give
 * ~23Hz per bin, enough to place the band edges accurately. Time-domain reads take
 * a shorter slice out of the same buffer (see below), so the long FFT costs nothing
 * in envelope responsiveness.
 */
export const ANALYSER_FFT_SIZE = 2048;

/**
 * ~11ms at 48kHz. The envelope is read over this much of the most recent audio.
 * Long enough to be a stable energy estimate, short enough that a decay is not
 * smeared: a window of W milliseconds caps the steepest measurable slope at roughly
 * 20dB/W, so the full 43ms buffer would flatten near-field decay into far-field
 * territory and hide the very cue we are after.
 */
export const ENVELOPE_SAMPLES = 512;

/**
 * ~5ms at 48kHz, matching the window the level meter was always meant to use, so
 * the existing noise gate keeps its tuning. Only the precision changes: the meter
 * previously read 8-bit samples, quantised to steps of 1/128, against thresholds
 * only two or three steps apart.
 */
export const GATE_SAMPLES = 256;

/** Band edges in Hz, resolved to bin indices against the real sample rate. */
const BANDS = {
  low: [100, 250],
  mid: [300, 1000],
  high: [3000, 6000],
};

/** Reported instead of -Infinity when a frame carries no measurable energy. */
export const SILENCE_DB = -120;

const MIN_RMS = 10 ** (SILENCE_DB / 20);
const MIN_ENERGY = 1e-12;

export const SEGMENT_DEFAULTS = {
  /** ~15s at 60fps. Bounds memory if VAD never closes a segment. */
  maxFrames: 900,
  /** A frame within this much of the peak counts as speech rather than a gap. */
  activeWindowDb: 12,
  /** Offset search: the last frame this close to the peak starts the decay. */
  offsetDropDb: 6,
  /** Stop measuring decay once energy has fallen this far. */
  decayTargetDb: 20,
  /** ...or after this long, whichever comes first. */
  decayWindowMs: 500,
};

const toDb = (amplitude) => 20 * Math.log10(Math.max(amplitude, MIN_RMS));

const clampBin = (bin, binCount) => Math.min(binCount - 1, Math.max(0, bin));

/**
 * Resolves the band edges to FFT bin indices. Sample rate is read from the audio
 * context rather than assumed, because 44.1kHz devices are common and a hard-coded
 * 48kHz would shift every band by about 9%.
 */
export function createBandBins(sampleRate, fftSize = ANALYSER_FFT_SIZE) {
  const binCount = fftSize / 2;
  const binWidth = sampleRate / fftSize;
  const bins = {};
  Object.keys(BANDS).forEach((name) => {
    const [fromHz, toHz] = BANDS[name];
    bins[name] = {
      from: clampBin(Math.round(fromHz / binWidth), binCount),
      to: clampBin(Math.round(toHz / binWidth), binCount),
    };
  });
  return bins;
}

/**
 * Reusable buffers so the metering loop does not allocate on every frame.
 * Falls back to byte reads on browsers without float time-domain support; the
 * 8-bit quantisation costs precision near the noise floor but keeps voice working.
 */
export function createFrameScratch(analyser) {
  return {
    time: new Float32Array(analyser.fftSize),
    timeBytes: new Uint8Array(analyser.fftSize),
    freq: new Float32Array(analyser.frequencyBinCount),
    hasFloatTime: typeof analyser.getFloatTimeDomainData === 'function',
    hasFloatFreq: typeof analyser.getFloatFrequencyData === 'function',
  };
}

/**
 * Fills the scratch buffer with the analyser's current window, normalising the
 * byte fallback so callers only deal with floats.
 */
const fillTimeDomain = (analyser, scratch) => {
  const total = analyser.fftSize;
  if (scratch.time.length !== total) return null;
  if (scratch.hasFloatTime) {
    analyser.getFloatTimeDomainData(scratch.time);
    return scratch.time;
  }
  const bytes = scratch.timeBytes.length === total ? scratch.timeBytes : new Uint8Array(total);
  analyser.getByteTimeDomainData(bytes);
  for (let i = 0; i < total; i += 1) {
    scratch.time[i] = (bytes[i] - 128) / 128;
  }
  return scratch.time;
};

/** The analyser writes oldest-to-newest, so the freshest audio is at the end. */
const rmsOfTail = (buffer, sampleCount) => {
  const total = buffer.length;
  const count = Math.min(sampleCount > 0 ? sampleCount : total, total);
  let sum = 0;
  for (let i = total - count; i < total; i += 1) {
    sum += buffer[i] * buffer[i];
  }
  return Math.sqrt(sum / count);
};

/** RMS amplitude in 0..1 over the most recent `sampleCount` samples. */
export function readRms(analyser, scratch, sampleCount) {
  if (!analyser || !scratch) return 0;
  const buffer = fillTimeDomain(analyser, scratch);
  if (!buffer) return 0;
  return rmsOfTail(buffer, sampleCount);
}

/**
 * Sums linear power across a band. getFloatFrequencyData reports dB per bin, so
 * each has to leave the log domain before it can be added.
 */
const bandEnergy = (freqDb, band) => {
  let sum = 0;
  for (let i = band.from; i <= band.to; i += 1) {
    const db = freqDb[i];
    if (Number.isFinite(db)) sum += 10 ** (db / 10);
  }
  return sum;
};

const bandRatioDb = (freqDb, band, reference) => {
  const numerator = bandEnergy(freqDb, band);
  const denominator = bandEnergy(freqDb, reference);
  if (denominator <= MIN_ENERGY) return null;
  return 10 * Math.log10((numerator + MIN_ENERGY) / denominator);
};

/**
 * One frame of measurements. `gateRms` is the short window the existing noise gate
 * consumes; `rms` is the slightly longer envelope window the decay and modulation
 * features are built from. Both come from a single buffer read.
 */
export function readFrameFeatures(analyser, scratch, bins) {
  if (!analyser || !scratch) return null;
  const buffer = fillTimeDomain(analyser, scratch);
  if (!buffer) return null;

  const rms = rmsOfTail(buffer, ENVELOPE_SAMPLES);
  const frame = {
    rms,
    gateRms: rmsOfTail(buffer, GATE_SAMPLES),
    db: toDb(rms),
    hfDb: null,
    lfDb: null,
  };

  if (!bins || !scratch.hasFloatFreq || scratch.freq.length !== analyser.frequencyBinCount) {
    return frame;
  }
  analyser.getFloatFrequencyData(scratch.freq);
  frame.hfDb = bandRatioDb(scratch.freq, bins.high, bins.mid);
  frame.lfDb = bandRatioDb(scratch.freq, bins.low, bins.mid);
  return frame;
}

const percentile = (sortedValues, p) => {
  if (!sortedValues.length) return null;
  const idx = Math.round((p / 100) * (sortedValues.length - 1));
  return sortedValues[Math.min(sortedValues.length - 1, Math.max(0, idx))];
};

const median = (values) => {
  if (!values.length) return null;
  const sorted = values.slice().sort((a, b) => a - b);
  return percentile(sorted, 50);
};

/**
 * dB per second of fall starting at `fromIdx`. Positive means decaying. Returns
 * null when there are not enough frames after the offset to fit a slope.
 */
const measureDecay = (frames, fromIdx, options) => {
  const start = frames[fromIdx];
  if (!start) return { slope: null, frames: 0, ms: 0 };
  const target = start.db - options.decayTargetDb;
  let end = null;
  for (let i = fromIdx + 1; i < frames.length; i += 1) {
    const frame = frames[i];
    if (end && frame.t - start.t > options.decayWindowMs) break;
    end = frame;
    if (frame.db <= target) break;
  }
  if (!end) return { slope: null, frames: 0, ms: 0 };
  const elapsedMs = end.t - start.t;
  if (elapsedMs <= 0) return { slope: null, frames: 0, ms: 0 };
  return {
    slope: ((start.db - end.db) / elapsedMs) * 1000,
    frames: frames.indexOf(end) - fromIdx,
    ms: elapsedMs,
  };
};

const measureRise = (frames, onsetIdx, options) => {
  const end = frames[onsetIdx];
  if (!end || onsetIdx === 0) return { slope: null, ms: 0 };
  const target = end.db - options.decayTargetDb;
  let start = frames[0];
  for (let i = onsetIdx - 1; i >= 0; i -= 1) {
    start = frames[i];
    if (frames[i].db <= target || end.t - frames[i].t > options.decayWindowMs) break;
  }
  const elapsedMs = end.t - start.t;
  if (elapsedMs <= 0) return { slope: null, ms: 0 };
  return { slope: ((end.db - start.db) / elapsedMs) * 1000, ms: elapsedMs };
};

/**
 * Reduces a burst of frames to the feature vector. Pure and side-effect free so it
 * can be replayed over recorded frames offline while tuning thresholds.
 */
export function summariseFrames(frames, options = SEGMENT_DEFAULTS) {
  if (!Array.isArray(frames) || frames.length < 2) return null;
  const opts = { ...SEGMENT_DEFAULTS, ...options };

  const dbs = frames.map((f) => f.db);
  const sorted = dbs.slice().sort((a, b) => a - b);
  const peakDb = sorted[sorted.length - 1];
  const p10Db = percentile(sorted, 10);
  const p90Db = percentile(sorted, 90);

  // Tilt is only meaningful while someone is actually talking. Averaging over the
  // silent gaps would measure the room's noise spectrum instead of the voice.
  const activeFloor = peakDb - opts.activeWindowDb;
  const active = frames.filter((f) => f.db >= activeFloor);
  const hfValues = active.map((f) => f.hfDb).filter((v) => Number.isFinite(v));
  const lfValues = active.map((f) => f.lfDb).filter((v) => Number.isFinite(v));

  const offsetFloor = peakDb - opts.offsetDropDb;
  let offsetIdx = 0;
  let onsetIdx = 0;
  let seenLoud = false;
  for (let i = 0; i < frames.length; i += 1) {
    if (frames[i].db >= offsetFloor) {
      offsetIdx = i;
      if (!seenLoud) {
        onsetIdx = i;
        seenLoud = true;
      }
    }
  }

  const decay = measureDecay(frames, offsetIdx, opts);
  const rise = measureRise(frames, onsetIdx, opts);

  return {
    frameCount: frames.length,
    durationMs: frames[frames.length - 1].t - frames[0].t,
    peakRms: Math.max(...frames.map((f) => f.rms)),
    peakDb,
    p10Db,
    p90Db,
    /** Syllabic dynamic range. Near speech ~20-30dB, far speech ~6-12dB. */
    modulationDepthDb: p90Db - p10Db,
    /** Primary proximity cue. Near speech is steep; a reverberant room is not. */
    decayDbPerSec: decay.slope,
    decayFrames: decay.frames,
    decayMs: decay.ms,
    riseDbPerSec: rise.slope,
    activeFrames: active.length,
    hfDbMedian: median(hfValues),
    lfDbMedian: median(lfValues),
  };
}

/**
 * Accumulates frames between VAD speech_started and speech_stopped.
 *
 * Driven unconditionally, including for turns the gate goes on to ignore — those
 * are the false triggers worth measuring, so they must not be filtered out before
 * they are recorded.
 */
export default class VoiceFeatureSegments {
  constructor(options = {}) {
    this.options = { ...SEGMENT_DEFAULTS, ...options };
    this.reset();
  }

  reset() {
    this.segment = null;
    this.nextId = 1;
  }

  get active() {
    return !!this.segment;
  }

  begin(nowMs, context = {}) {
    this.segment = {
      id: this.nextId,
      startedMs: nowMs,
      context,
      frames: [],
      dropped: 0,
      bargeInFired: false,
    };
    this.nextId += 1;
    return this.segment.id;
  }

  note(frame, nowMs) {
    if (!this.segment || !frame) return;
    if (this.segment.frames.length >= this.options.maxFrames) {
      this.segment.dropped += 1;
      return;
    }
    this.segment.frames.push({
      t: nowMs - this.segment.startedMs,
      rms: frame.rms,
      db: frame.db,
      hfDb: frame.hfDb,
      lfDb: frame.lfDb,
    });
  }

  /** Records that the client acted on this segment as a barge-in. */
  markBargeIn() {
    if (this.segment) this.segment.bargeInFired = true;
  }

  finish(nowMs) {
    const segment = this.segment;
    this.segment = null;
    if (!segment) return null;
    return {
      id: segment.id,
      context: segment.context,
      bargeInFired: segment.bargeInFired,
      droppedFrames: segment.dropped,
      wallDurationMs: Math.max(0, nowMs - segment.startedMs),
      features: summariseFrames(segment.frames, this.options),
    };
  }
}
